import type { PublicPageSeo } from '@apuestapr/contracts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

import { isAbortedResult, type PublicApi } from '../api/public-api.ts';
import type { PublicWebConfig } from './config.ts';

const ownedAttribute = 'data-apr-head';

export const defaultDocumentHead = Object.freeze({
  title: 'ApuestaPR | Apuestas deportivas en Puerto Rico',
  description: 'Información, promociones, noticias y ayuda de ApuestaPR.',
  keywords: ['ApuestaPR', 'apuestas deportivas', 'Puerto Rico'],
  robots: 'index,follow',
});

export interface DocumentHead {
  readonly title: string;
  readonly description: string;
  readonly keywords: readonly string[];
  readonly robots: string;
  readonly canonical: string;
  readonly ogTitle: string;
  readonly ogDescription: string;
  readonly ogImageUrl: string | null;
}

const appendMeta = (
  target: Document,
  attribute: 'name' | 'property',
  key: string,
  content: string,
): void => {
  const meta = target.createElement('meta');
  meta.setAttribute(ownedAttribute, '');
  meta.setAttribute(attribute, key);
  meta.content = content;
  target.head.append(meta);
};

/** Replace only the metadata this application explicitly owns. */
export const reconcileDocumentHead = (target: Document, head: DocumentHead): void => {
  target.head.querySelectorAll(`[${ownedAttribute}]`).forEach((element) => {
    element.remove();
  });

  const title = target.createElement('title');
  title.setAttribute(ownedAttribute, '');
  title.textContent = head.title;
  target.head.append(title);

  appendMeta(target, 'name', 'description', head.description);
  if (head.keywords.length > 0) {
    appendMeta(target, 'name', 'keywords', head.keywords.join(', '));
  }
  appendMeta(target, 'name', 'robots', head.robots);

  const canonical = target.createElement('link');
  canonical.setAttribute(ownedAttribute, '');
  canonical.rel = 'canonical';
  canonical.href = head.canonical;
  target.head.append(canonical);

  appendMeta(target, 'property', 'og:type', 'website');
  appendMeta(target, 'property', 'og:title', head.ogTitle);
  appendMeta(target, 'property', 'og:description', head.ogDescription);
  appendMeta(target, 'property', 'og:url', head.canonical);
  if (head.ogImageUrl !== null) appendMeta(target, 'property', 'og:image', head.ogImageUrl);

  appendMeta(
    target,
    'name',
    'twitter:card',
    head.ogImageUrl === null ? 'summary' : 'summary_large_image',
  );
  appendMeta(target, 'name', 'twitter:title', head.ogTitle);
  appendMeta(target, 'name', 'twitter:description', head.ogDescription);
  if (head.ogImageUrl !== null) appendMeta(target, 'name', 'twitter:image', head.ogImageUrl);
};

const canonicalFor = (origin: string, pathname: string): string => {
  const canonical = new URL(`${origin}${pathname}`);
  canonical.search = '';
  canonical.hash = '';
  return canonical.href;
};

const headFromSeo = (seo: PublicPageSeo, canonical: string): DocumentHead => ({
  title: seo.title,
  description: seo.description,
  keywords: seo.keywords,
  robots: seo.robots,
  canonical,
  ogTitle: seo.ogTitle ?? seo.title,
  ogDescription: seo.ogDescription ?? seo.description,
  ogImageUrl: seo.ogImageUrl,
});

const fallbackHead = (canonical: string, routeTitle: string | null): DocumentHead => ({
  ...defaultDocumentHead,
  title: routeTitle ?? defaultDocumentHead.title,
  canonical,
  ogTitle: routeTitle ?? defaultDocumentHead.title,
  ogDescription: defaultDocumentHead.description,
  ogImageUrl: null,
});

type SeoSnapshot =
  | { readonly pathname: string; readonly status: 'fallback' }
  | { readonly pathname: string; readonly status: 'ready'; readonly seo: PublicPageSeo };

interface RouteTitleFallbackValue {
  readonly pathname: string;
  readonly title: string;
}

interface RouteTitleFallbackContextValue {
  readonly publish: (value: RouteTitleFallbackValue) => () => void;
}

const RouteTitleFallbackContext = createContext<RouteTitleFallbackContextValue | null>(null);

const RouteHead = ({
  api,
  config,
  routeTitle,
}: {
  readonly api: PublicApi;
  readonly config: PublicWebConfig;
  readonly routeTitle: RouteTitleFallbackValue | null;
}): null => {
  const { pathname } = useLocation();
  const [snapshot, setSnapshot] = useState<SeoSnapshot | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    void api
      .pageSeo(pathname, controller.signal)
      .then((result) => {
        if (controller.signal.aborted || isAbortedResult(result)) return;
        setSnapshot(
          result.ok && result.data.path === pathname
            ? { pathname, status: 'ready', seo: result.data }
            : { pathname, status: 'fallback' },
        );
      })
      .catch(() => {
        if (!controller.signal.aborted) setSnapshot({ pathname, status: 'fallback' });
      });

    return () => {
      controller.abort();
    };
  }, [api, pathname]);

  const head = useMemo(() => {
    const canonical = canonicalFor(config.canonicalOrigin, pathname);
    if (snapshot?.pathname === pathname && snapshot.status === 'ready') {
      return headFromSeo(snapshot.seo, canonical);
    }
    const fallbackTitle = routeTitle?.pathname === pathname ? routeTitle.title : null;
    return fallbackHead(canonical, fallbackTitle);
  }, [config.canonicalOrigin, pathname, routeTitle, snapshot]);

  useEffect(() => {
    reconcileDocumentHead(document, head);
  }, [head]);

  return null;
};

export const RouteHeadProvider = ({
  api,
  config,
  children,
}: {
  readonly api: PublicApi;
  readonly config: PublicWebConfig;
  readonly children: ReactNode;
}): ReactElement => {
  const [routeTitle, setRouteTitle] = useState<RouteTitleFallbackValue | null>(null);
  const publish = useCallback((value: RouteTitleFallbackValue): (() => void) => {
    setRouteTitle(value);
    return () => {
      setRouteTitle((current) => (current === value ? null : current));
    };
  }, []);
  const context = useMemo<RouteTitleFallbackContextValue>(() => ({ publish }), [publish]);

  return (
    <RouteTitleFallbackContext.Provider value={context}>
      <RouteHead api={api} config={config} routeTitle={routeTitle} />
      {children}
    </RouteTitleFallbackContext.Provider>
  );
};

/** Publish a title already loaded by route content; RouteHead uses it only while SEO falls back. */
export const RouteTitleFallback = ({ title }: { readonly title: string }): null => {
  const context = useContext(RouteTitleFallbackContext);
  const { pathname } = useLocation();

  useEffect(() => {
    if (context === null) return;
    return context.publish({ pathname, title });
  }, [context, pathname, title]);

  return null;
};
